import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const SEV_LABEL = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

/**
 * Small "AI Summarize" button — pops a dialog with summary + severity + action items.
 * `kind` is "maintenance" or "incident". `text` and `title` are the report content.
 * `onApplySeverity(sev)` (optional) lets the caller apply the suggested severity/priority.
 */
export default function AiSummarizeButton({ kind, title, text, onApplySeverity, disabled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text || !text.trim()) {
      toast.error("Nothing to summarize yet.");
      return;
    }
    setOpen(true);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/summarize-issue", { text: text.trim(), kind, title: title || undefined });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI request failed");
      setOpen(false);
    }
    setBusy(false);
  };

  return (
    <>
      <Button
        type="button"
        data-testid="ai-summarize-btn"
        variant="outline"
        size="sm"
        onClick={run}
        disabled={disabled}
        className="gap-1.5 border-gold/50 text-ink hover:border-gold hover:bg-gold/5"
      >
        <Sparkles className="h-3.5 w-3.5 text-gold" />
        <span className="uppercase tracking-[0.15em] text-[10px] font-semibold">AI Summary</span>
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="ai-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-gold" /> AI analysis
            </DialogTitle>
          </DialogHeader>
          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Analysing report…
            </div>
          )}
          {!busy && result && (
            <div className="space-y-4 pt-2">
              <div>
                <div className="micro-label text-gold mb-1.5">Summary</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="ai-summary-text">{result.summary}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="micro-label text-gold">Suggested {kind === "maintenance" ? "priority" : "severity"}</div>
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full border border-gold/40 bg-gold/10 text-ink font-semibold uppercase tracking-wider"
                  data-testid="ai-suggested-severity"
                >
                  {SEV_LABEL[result.suggested_severity] || result.suggested_severity}
                </span>
                {onApplySeverity && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="ai-apply-severity"
                    onClick={() => { onApplySeverity(result.suggested_severity); toast.success("Applied"); }}
                    className="ml-auto rounded-none uppercase tracking-[0.15em] text-[10px] font-semibold"
                  >
                    Apply
                  </Button>
                )}
              </div>
              {result.action_items?.length > 0 && (
                <div>
                  <div className="micro-label text-gold mb-1.5">Next steps</div>
                  <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5" data-testid="ai-action-items">
                    {result.action_items.map((a, i) => (<li key={i}>{a}</li>))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
